import {
  IconButton,
  List,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { Controller, useFieldArray, type Control } from 'react-hook-form';
import { z } from 'zod';

import { type DocumentType, graphql } from '@/gql';
import { nonFalsey } from '@/utils/nonFalsey';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';
import { Or2ListItemButton } from '@/components/data-display/Or2ListItemButton';

export const AutoCreateFoldersSchema = z.object({
  newCommittees: z.array(
    z.object({
      name: z.string().trim().min(1, 'Committee name is required'),
    }),
  ),
});

export type AutoCreateFoldersFormData = z.infer<typeof AutoCreateFoldersSchema>;

const FILE_MANAGER_AUTO_CREATE_FOLDERS_FORM_COMMITTEE_FRAGMENT = graphql(`
  fragment fileManagerAutoCreateFoldersFormCommitteeFields on Group {
    _id
    name
  }
`);

interface Props {
  committees:
    | Array<
        | DocumentType<
            typeof FILE_MANAGER_AUTO_CREATE_FOLDERS_FORM_COMMITTEE_FRAGMENT
          >
        | null
        | undefined
      >
    | null
    | undefined;
  control: Control<AutoCreateFoldersFormData>;
}

export const FileManagerAutoCreateFoldersForm = ({
  committees,
  control,
}: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'newCommittees',
  });

  return (
    <List>
      {committees
        ? committees.filter(nonFalsey).map((committee) => {
            return (
              <Or2ListItem key={committee._id}>
                <ListItemAvatar>
                  <GroupOrServiceAvatar
                    iconUrl={null}
                    groupOrServiceName={committee.name}
                  />
                </ListItemAvatar>
                <ListItemText primary={committee.name} />
              </Or2ListItem>
            );
          })
        : null}
      {fields.map((committee, index) => {
        return (
          <Or2ListItem key={committee.id}>
            <Controller
              name={`newCommittees.${index}.name`}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <ListItemAvatar>
                    <GroupOrServiceAvatar
                      iconUrl={null}
                      groupOrServiceName={value}
                    />
                  </ListItemAvatar>
                  <TextField
                    placeholder="Committee name"
                    helperText={error ? error.message : null}
                    size="small"
                    error={!!error}
                    onChange={onChange}
                    value={value}
                    fullWidth
                    label="Name"
                  />
                </>
              )}
            />
            <IconButton sx={{ marginLeft: 1 }} onClick={() => remove(index)}>
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </Or2ListItem>
        );
      })}
      <Or2ListItemButton
        sx={{ alignItems: 'center' }}
        onClick={() => append({ name: '' })}
      >
        <ListItemIcon>
          <AddOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Add committee" />
      </Or2ListItemButton>
    </List>
  );
};
